import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Link,
  CircularProgress,
} from '@mui/material';
import api from '../../services/api';

/**
 * Blocking interstitial for express accounts: second explicit terms confirmation
 * at first Mailbox login. Skipped when MAILBOX_TERMS already recorded for the
 * current document version.
 */
export default function MailboxTermsGate() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;

    const run = async () => {
      attempts += 1;
      try {
        // api.get already returns the JSON body (not AxiosResponse).
        const data = await api.get<{ required?: boolean }>('/api/Consent/mailbox-terms-status', {
          timeout: 12_000,
        });
        if (cancelled) return;
        if (data?.required) setOpen(true);
        setChecking(false);
      } catch {
        // Token may not be hydrated on the very first tick after SPA boot; retry once.
        if (!cancelled && attempts < 2) {
          window.setTimeout(() => {
            void run();
          }, 400);
          return;
        }
        if (!cancelled) setChecking(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, []);

  async function accept() {
    setBusy(true);
    try {
      await api.post('/api/Consent/mailbox-terms');
      setOpen(false);
    } catch {
      /* keep open */
    } finally {
      setBusy(false);
    }
  }

  if (checking || !open) return null;

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      aria-labelledby="mailbox-terms-title"
      data-testid="mailbox-terms-gate"
    >
      <DialogTitle id="mailbox-terms-title">Confirm Terms of Service</DialogTitle>
      <DialogContent>
        <Typography paragraph>
          Before using your mailbox, please confirm you agree to the Qixit Terms of Service and
          Privacy Policy.
        </Typography>
        <Typography variant="body2">
          <Link href="https://qixit.com/terms" target="_blank" rel="noopener">
            Terms of Service
          </Link>
          {' · '}
          <Link href="https://qixit.com/privacy" target="_blank" rel="noopener">
            Privacy Policy
          </Link>
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          onClick={accept}
          disabled={busy}
          data-testid="mailbox-terms-accept"
        >
          {busy ? <CircularProgress size={18} /> : 'I Agree'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
